import type {JSX, ReactNode} from 'react';

import type {AfriexTransactionStatus} from '@/types';
import {statusIcons} from '@/types/Transaction';
import {capitalizeWords} from '@/utils/dashboard';

type BadgeProps = {
  label?: string | AfriexTransactionStatus;
  colorScheme?: string;
  color?: string;
  background?: string;
  className?: string;
  children?: string | JSX.Element | JSX.Element[] | ReactNode;
  dataCy?: string;
};

export default function Badge({
  colorScheme = 'gray',
  color,
  background,
  className = '',
  label,
  children,
  dataCy,
}: BadgeProps): JSX.Element {
  // Determine background and text color classes
  let bgClass = background ? background : `bg-${colorScheme}-100`;
  let textClass = color ? color : `text-${colorScheme}-500`;

  // For dynamic color schemes, we'll need to handle different cases
  if (!background && colorScheme) {
    switch (colorScheme) {
      case 'gray':
        bgClass = 'bg-gray-100 dark:bg-gray-900';
        break;
      case 'pending':
        bgClass = 'bg-pending-100 dark:bg-pending-900';
        break;
      case 'successful':
        bgClass = 'bg-successful-100 dark:bg-successful-900';
        break;
      case 'failed':
        bgClass = 'bg-failed-100 dark:bg-failed-900';
        break;
      case 'refunded':
        bgClass = 'bg-refunded-100 dark:bg-refunded-900';
        break;
      case 'indigo':
        bgClass = 'bg-indigo-100 dark:bg-indigo-900';
        break;
      case 'blue':
        bgClass = 'bg-blue-100';
        break;
      case 'green':
        bgClass = 'bg-green-100';
        break;
      case 'red':
        bgClass = 'bg-red-100';
        break;
      // Add other color schemes as needed
      default:
        bgClass = `bg-gray-100 dark:bg-[#1e2022]`;
    }
  }

  if (!color && colorScheme) {
    switch (colorScheme) {
      case 'gray':
        textClass = 'text-gray-500 dark:text-gray-400';
        break;
      case 'pending':
        textClass = 'text-pending-500 dark:text-pending-200';
        break;
      case 'successful':
        textClass = 'text-successful-500 dark:text-successful-200';
        break;
      case 'failed':
        textClass = 'text-failed-500 dark:text-failed-200';
        break;
      case 'refunded':
        textClass = 'text-refunded-500 dark:text-refunded-200';
        break;
      case 'indigo':
        textClass = 'text-indigo-500 dark:text-indigo-800';
        break;
      case 'blue':
        textClass = 'text-blue-500';
        break;
      case 'green':
        textClass = 'text-green-500';
        break;
      case 'red':
        textClass = 'text-red-500';
        break;
      // Add other color schemes as needed
      default:
        textClass = `text-gray-500 dark:text-gray-400`;
    }
  }

  // Read the module-level icon registry directly. Going through a getIcon()
  // wrapper hid the lookup behind a call, which reads as "component built here"
  // to any caller (and to the React compiler) even though every value in the
  // registry is a stable module-level component.
  const Icon = statusIcons[label as AfriexTransactionStatus];

  return (
    <span
      data-cy={dataCy}
      className={`py-1 px-2 inline-flex items-center gap-1 text-sm capitalize font-normal leading-5 rounded-xl ${bgClass} ${textClass} ${className}`}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {label && capitalizeWords(label?.replaceAll(/_/g, ' '))}
      {children}
    </span>
  );
}
